import {IssueTracker, type SignalArtifact} from "@devplaninc/codeup"
import {discussionTopicArtifactProvider} from "@/app/components/signals/signalKinds"

export type EvidenceProvider =
  | "slack"
  | "teams"
  | "zoom"
  | "jira"
  | "linear"
  | "ticket"
  | "change"
  | "github"
  | "bitbucket"
  | "gitlab"
  | "signal"
  | "insight"
  | "discussion"
  | "notion"
  | "confluence"
  | "google_drive"
  | "upload"
  | "granola"
  | "otter"
  | "call_note"
  | "text_note"
  | "feature"
  | "project"
  | "document"
  | "unknown"

const EVIDENCE_PROVIDER_LABELS: Record<EvidenceProvider, string> = {
  slack: "Slack",
  teams: "Microsoft Teams",
  zoom: "Zoom",
  jira: "Jira",
  linear: "Linear",
  ticket: "Ticket",
  change: "Change",
  github: "GitHub",
  bitbucket: "Bitbucket",
  gitlab: "GitLab",
  signal: "Signal",
  insight: "Insight",
  discussion: "Discussion",
  notion: "Notion",
  confluence: "Confluence",
  google_drive: "Google Docs",
  upload: "Uploaded document",
  granola: "Granola",
  otter: "Otter",
  call_note: "Call note",
  text_note: "Text note",
  feature: "Feature",
  project: "Project",
  document: "Document",
  unknown: "Source",
}

export function evidenceProviderLabel(provider: EvidenceProvider): string {
  return EVIDENCE_PROVIDER_LABELS[provider] ?? EVIDENCE_PROVIDER_LABELS.unknown
}

export function artifactEvidenceProvider(artifact: SignalArtifact): EvidenceProvider {
  const details = artifact.details
  if (!details) return "unknown"

  switch (details.$case) {
    case "discussionTopic":
      return discussionTopicArtifactProvider(artifact) ?? "discussion"
    case "issue":
      switch (details.value.tracker) {
        case IssueTracker.ISSUE_TRACKER_JIRA:
          return "jira"
        case IssueTracker.ISSUE_TRACKER_LINEAR:
          return "linear"
        default:
          return "ticket"
      }
    case "document":
      return documentEvidenceProvider(details.value.provider)
    case "callNote":
      return callNoteEvidenceProvider(details.value.providerType)
    case "feature":
      return "feature"
    default:
      return "unknown"
  }
}

export function evidenceProviderFromSourceType(sourceType: string): EvidenceProvider {
  switch (sourceType) {
    case "discussion_topic":
      return "discussion"
    case "jira":
      return "jira"
    case "linear":
      return "linear"
    case "ticket":
      return "ticket"
    case "call_note":
      return "call_note"
    case "feature":
      return "feature"
    case "document":
      return "document"
    default:
      return "unknown"
  }
}

export function documentEvidenceProvider(provider: string): EvidenceProvider {
  switch (provider.trim().toLowerCase()) {
    case "upload":
    case "uploaded":
      return "upload"
    case "drive":
    case "google_drive":
    case "google-drive":
      return "google_drive"
    case "notion":
      return "notion"
    case "confluence":
      return "confluence"
    default:
      return "document"
  }
}

function callNoteEvidenceProvider(providerType: string): EvidenceProvider {
  switch (providerType.trim().toLowerCase()) {
    case "teams_meeting":
      return "teams"
    case "zoom":
      return "zoom"
    case "granola":
      return "granola"
    case "otter":
      return "otter"
    default:
      return "call_note"
  }
}
